import React, { useState } from 'react';
import { Modal, Box, Typography, Button, IconButton, TextField, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import modalStyle from '../../styles/modalStyle';
import getCoverageColDef from '../../colDef/coverageColumnDef';
import Coverage from '../../interfaces/coverage';
import CoverageService from '../../services/coverageService';

interface BulkRejectCoverageModalProps {
    open: boolean;
    onClose: () => void;
    selectedRows: Coverage[];
    onSuccess: () => void;
}

const BulkRejectCoverageModal: React.FC<BulkRejectCoverageModalProps> = ({ open, onClose, selectedRows, onSuccess }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [rejectComment, setRejectComment] = useState<string>('');

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const promises = selectedRows.map(coverage => 
                CoverageService.updateCoverageStatus(coverage.id, {
                    status: "REJECTED",
                    comment: rejectComment.trim() || undefined
                })
            );
            
            await Promise.all(promises);
            alert(`Successfully rejected ${selectedRows.length} coverage${selectedRows.length > 1 ? 's' : ''}`);
            onClose();
            onSuccess();
        } catch (error) {
            console.error('Error rejecting coverage: ', error);
            alert("Failed to reject coverage");
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const handleClose = () => {
        setRejectComment('');
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                        <CircularProgress size={50} />
                    </Box>
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Reject Coverage</Typography>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                            Are you sure you want to reject {selectedRows.length} coverage{selectedRows.length > 1 ? 's' : ''}?
                        </Typography>
                        <TextField
                            label="Comment (Optional)"
                            value={rejectComment}
                            onChange={(e) => setRejectComment(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter optional comment for rejection"
                            sx={{ mb: 2 }}
                        />
                        <Box mt={3}>
                            <Typography variant="subtitle1">Selected Rows:</Typography>
                            <SearchResultGrid
                                getRowId={(row: Coverage) => row.id}
                                results={selectedRows}
                                onSelectRow={() => {}}
                                columns={getCoverageColDef()}
                                isRowSelectionAllowed={false}
                            />
                        </Box>
                        <Box sx={{ mt: 3 }} display="flex" justifyContent="flex-end" gap={1}>
                            <Button variant="outlined" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Confirm
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default BulkRejectCoverageModal;
